// Eligible Task Dispatcher — query the eligibility surface for a given
// agent identity and dispatch tasks per-row or in bulk.
//
// Engine routes used:
//   GET  /api/v1/tasks/agent/eligible       → 4-bucket EligibilityResponse
//   POST /api/v1/agent-runs                 → create the run row
//   POST /api/v1/tasks/{task_uuid}/dispatch → bind to HarnessWorkflow
//   POST /api/v1/tasks/agent/bulk-dispatch  → orchestrated dry-run / live
//
// Live bulk-dispatch is gated by a HonestUnavailableCard overlay until
// end-to-end smoke is performed and Director-approved (Lane 2 follow-up).
//
// Invariant: bulk-dispatch ALWAYS issues dry_run=true first. Operators
// must explicitly flip the "Run live" toggle to issue dry_run=false.

import { useState } from 'react';
import useSWR from 'swr';
import {
  bulkDispatchAgentTasks,
  dispatchTask,
  listEligibleTasks,
} from '@/api/agentTasks';
import { createAgentRun } from '@/api/agentRuns';
import type {
  BulkDispatchResponse,
  EligibilityResponse,
  EligibilityTaskRef,
} from '@/api/agentBoardTypes';
import { HonestUnavailableCard } from '@/components/HonestUnavailableCard';
import { EngineHttpError } from '@/lib/engineHttp';
import { useEscapeKey } from '@/lib/useEscapeKey';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

const SWR_KEY = 'agent-board:eligible';

interface PerTaskDispatch {
  task: EligibilityTaskRef;
}

function fetcher(): Promise<EligibilityResponse> {
  return listEligibleTasks();
}

function EligibleTaskDispatcherWidget({ context }: WidgetProps) {
  const { data, error, isLoading, mutate } = useSWR<EligibilityResponse>(
    SWR_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [agentId, setAgentId] = useState('claude-ide');
  const [perTaskModal, setPerTaskModal] = useState<PerTaskDispatch | null>(null);
  const [perTaskSubmitting, setPerTaskSubmitting] = useState(false);
  const [perTaskError, setPerTaskError] = useState<string | null>(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunLive, setBulkRunLive] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkDispatchResponse | null>(null);

  async function handlePerTaskDispatch() {
    if (!perTaskModal) return;
    const taskUuid = perTaskModal.task.task_uuid;
    if (!taskUuid) {
      setPerTaskError('Task is missing task_uuid; cannot dispatch.');
      return;
    }
    setPerTaskSubmitting(true);
    setPerTaskError(null);
    try {
      const run = await createAgentRun({
        agent_id: agentId.trim(),
        task_id: taskUuid,
      });
      await dispatchTask(taskUuid, { agent_run_id: run.id });
      setPerTaskModal(null);
      void mutate();
    } catch (err) {
      setPerTaskError(
        err instanceof EngineHttpError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error',
      );
      setPerTaskSubmitting(false);
    }
  }

  // Bulk dispatch always sends dry_run=true unless the operator explicitly
  // toggles "Run live" (which is itself behind a HonestUnavailableCard
  // overlay until smoke ships).
  async function handleBulkDispatch(runLive: boolean) {
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkResult(null);
    try {
      const result = await bulkDispatchAgentTasks({
        agent_identity_id: agentId.trim() || undefined,
        dry_run: !runLive,
        dispatch_mode: 'eligible_only',
      });
      setBulkResult(result);
      void mutate();
    } catch (err) {
      setBulkError(
        err instanceof EngineHttpError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error',
      );
    } finally {
      setBulkSubmitting(false);
    }
  }

  return (
    <div id="widget-eligible-task-dispatcher" className="sp-widget">
      <div className="sp-widget-header">
        <span>Eligible Task Dispatcher</span>
        <input
          type="text"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          aria-label="Agent identifier for dispatch"
          placeholder="agent-id"
          style={{
            background: 'transparent',
            border: '1px solid var(--sp-border)',
            borderRadius: 3,
            padding: '2px 6px',
            color: 'var(--sp-text)',
            fontSize: 11,
            fontFamily: 'inherit',
            width: 120,
          }}
        />
      </div>

      <div className="sp-widget-body" style={{ padding: 12, fontSize: 12 }}>
        {error ? (
          <div style={{ color: 'var(--sp-error)' }}>
            {error instanceof Error ? error.message : 'Failed to load eligibility.'}
          </div>
        ) : isLoading || !data ? (
          <div style={{ color: 'var(--sp-text-muted)' }}>Loading eligibility…</div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <BucketColumn
                label="Eligible"
                color="var(--sp-ok)"
                items={data.eligible}
                actionLabel="Dispatch"
                onAction={(task) => {
                  setPerTaskModal({ task });
                  setPerTaskError(null);
                }}
              />
              <BucketColumn
                label="Approval required"
                color="var(--sp-warn)"
                items={data.approval_required}
              />
              <BucketColumn
                label="Blocked"
                color="var(--sp-text-dim)"
                items={data.blocked}
              />
              <BucketColumn
                label="Denied"
                color="var(--sp-error)"
                items={data.denied}
              />
            </div>

            <div style={{ borderTop: '1px solid var(--sp-border)', paddingTop: 8 }}>
              <button
                type="button"
                onClick={() => {
                  setBulkOpen(true);
                  setBulkRunLive(false);
                  setBulkError(null);
                  setBulkResult(null);
                }}
                style={{
                  fontSize: 11,
                  padding: '4px 12px',
                  border: '1px solid var(--sp-accent)',
                  background: 'transparent',
                  color: 'var(--sp-accent)',
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                Dispatch all eligible…
              </button>
            </div>
          </>
        )}
      </div>

      {perTaskModal ? (
        <ConfirmModal
          title="Dispatch task"
          body={
            <div style={{ fontSize: 12 }}>
              <div className="mono" style={{ fontSize: 11, marginBottom: 4 }}>
                {perTaskModal.task.task_uuid}
              </div>
              <div style={{ color: 'var(--sp-text-muted)' }}>
                {perTaskModal.task.title ?? perTaskModal.task.task_id ?? '(no title)'}
              </div>
              <div style={{ marginTop: 8, color: 'var(--sp-text-dim)', fontSize: 11 }}>
                A new AgentRun will be created and bound to a HarnessWorkflow under
                agent <span className="mono">{agentId}</span>.
              </div>
              {perTaskError ? (
                <div style={{ color: 'var(--sp-error)', fontSize: 11, marginTop: 8 }}>
                  {perTaskError}
                </div>
              ) : null}
            </div>
          }
          confirmLabel="Dispatch"
          confirmColor="var(--sp-accent)"
          submitting={perTaskSubmitting}
          onCancel={() => {
            setPerTaskModal(null);
            setPerTaskSubmitting(false);
          }}
          onConfirm={handlePerTaskDispatch}
        />
      ) : null}

      {bulkOpen ? (
        <BulkDispatchModal
          isDirector={context.isDirector}
          runLive={bulkRunLive}
          onRunLiveChange={setBulkRunLive}
          submitting={bulkSubmitting}
          error={bulkError}
          result={bulkResult}
          onCancel={() => {
            setBulkOpen(false);
            setBulkSubmitting(false);
            setBulkResult(null);
            setBulkError(null);
            setBulkRunLive(false);
          }}
          onSubmit={() => handleBulkDispatch(bulkRunLive)}
        />
      ) : null}
    </div>
  );
}

interface BucketColumnProps {
  label: string;
  color: string;
  items: EligibilityTaskRef[];
  actionLabel?: string;
  onAction?: (task: EligibilityTaskRef) => void;
}

function BucketColumn({ label, color, items, actionLabel, onAction }: BucketColumnProps) {
  return (
    <div
      style={{
        border: `1px solid var(--sp-border)`,
        borderTop: `2px solid ${color}`,
        borderRadius: 3,
        padding: 6,
        minHeight: 80,
      }}
    >
      <div
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color,
          marginBottom: 4,
        }}
      >
        {label} ({items.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.length === 0 ? (
          <div style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>—</div>
        ) : (
          items.map((task, idx) => {
            const id = (task.task_uuid as string | undefined) ?? `idx-${idx}`;
            return (
              <div
                key={id}
                style={{
                  border: '1px solid var(--sp-border)',
                  borderRadius: 3,
                  padding: '4px 6px',
                  fontSize: 11,
                }}
              >
                <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                  {task.task_id ?? task.task_uuid?.slice(0, 8) ?? '?'}
                </div>
                {task.title ? (
                  <div style={{ color: 'var(--sp-text)' }}>{task.title}</div>
                ) : null}
                {task.reason ? (
                  <div style={{ color: 'var(--sp-text-dim)', fontSize: 10, marginTop: 2 }}>
                    {task.reason}
                  </div>
                ) : null}
                {onAction && actionLabel ? (
                  <button
                    type="button"
                    onClick={() => onAction(task)}
                    style={{
                      marginTop: 4,
                      fontSize: 10,
                      padding: '2px 6px',
                      border: `1px solid ${color}`,
                      color,
                      background: 'transparent',
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                  >
                    {actionLabel}
                  </button>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

interface ConfirmModalProps {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmColor: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  confirmColor,
  submitting,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  useEscapeKey(() => {
    if (!submitting) onCancel();
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={modalShellStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div style={modalCardStyle}>
        <div style={modalTitleStyle}>{title}</div>
        <div style={{ marginBottom: 12 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={cancelButtonStyle(submitting)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: `1px solid ${confirmColor}`,
              background: confirmColor,
              color: 'var(--sp-bg, #000)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BulkDispatchModalProps {
  isDirector: boolean;
  runLive: boolean;
  onRunLiveChange: (next: boolean) => void;
  submitting: boolean;
  error: string | null;
  result: BulkDispatchResponse | null;
  onCancel: () => void;
  onSubmit: () => void;
}

function BulkDispatchModal({
  isDirector,
  runLive,
  onRunLiveChange,
  submitting,
  error,
  result,
  onCancel,
  onSubmit,
}: BulkDispatchModalProps) {
  useEscapeKey(() => {
    if (!submitting) onCancel();
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={modalShellStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div style={{ ...modalCardStyle, width: 560 }}>
        <div style={modalTitleStyle}>Bulk dispatch</div>

        <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--sp-text-muted)' }}>
          Dry-run is the default. The engine reports per-bucket counts without
          creating runs or approvals. Switch to "Run live" only when you intend
          to commit to the side-effects.
        </div>

        <label
          title={
            !isDirector
              ? 'Only the Director can issue a live bulk dispatch. Non-Director users may run dry-runs.'
              : undefined
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 8,
            cursor: !isDirector ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={runLive}
            onChange={(e) => onRunLiveChange(e.target.checked)}
            disabled={!isDirector || submitting}
            title={
              !isDirector
                ? 'Only the Director can issue a live bulk dispatch.'
                : undefined
            }
          />
          <span style={{ fontSize: 12, color: isDirector ? 'var(--sp-text)' : 'var(--sp-text-dim)' }}>
            Run live (dry_run=false)
          </span>
          {!isDirector ? (
            <span style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
              (Director only)
            </span>
          ) : null}
        </label>

        {runLive ? (
          <div style={{ marginBottom: 12 }}>
            <HonestUnavailableCard
              title="Live Bulk Dispatch — pending end-to-end smoke"
              route="POST /api/v1/tasks/agent/bulk-dispatch (dry_run=false)"
              handoffPath="docs/coordination/handoffs/2026-05-03-lane-2-agent-board-backend-followups.md"
              reason="Live bulk dispatch is implemented but has not been exercised end-to-end in production. Director must approve a smoke run before this overlay is removed."
            />
          </div>
        ) : null}

        {error ? (
          <div style={{ color: 'var(--sp-error)', fontSize: 11, marginBottom: 8 }}>{error}</div>
        ) : null}

        {result ? (
          <div
            style={{
              marginBottom: 12,
              padding: 8,
              border: '1px solid var(--sp-border)',
              borderRadius: 3,
              fontSize: 11,
            }}
          >
            <div style={{ marginBottom: 4 }}>
              {result.dry_run ? 'Dry run result' : 'Live dispatch result'}
            </div>
            <BulkBucketLine
              label="eligible_to_dispatch"
              count={result.eligible_to_dispatch.length}
            />
            <BulkBucketLine
              label="approval_requests_created"
              count={result.approval_requests_created.length}
            />
            <BulkBucketLine label="blocked" count={result.blocked.length} />
            <BulkBucketLine label="denied" count={result.denied.length} />
            <BulkBucketLine label="dispatched" count={result.dispatched.length} />
            <div className="mono" style={{ marginTop: 6, color: 'var(--sp-text-dim)' }}>
              idempotency_key: {result.idempotency_key}
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={cancelButtonStyle(submitting)}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            style={{
              fontSize: 11,
              padding: '4px 12px',
              border: `1px solid var(--sp-accent)`,
              background: 'var(--sp-accent)',
              color: 'var(--sp-bg, #000)',
              borderRadius: 3,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '…' : runLive ? 'Run live' : 'Run dry-run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkBucketLine({ label, count }: { label: string; count: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
      <span className="mono" style={{ color: 'var(--sp-text-dim)' }}>{label}</span>
      <span>{count}</span>
    </div>
  );
}

const modalShellStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalCardStyle: React.CSSProperties = {
  width: 480,
  background: 'var(--sp-bg-panel, #111)',
  border: '1px solid var(--sp-border)',
  borderRadius: 4,
  padding: 16,
  fontSize: 12,
  color: 'var(--sp-text)',
  maxHeight: '85vh',
  overflowY: 'auto',
};

const modalTitleStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--sp-text-dim)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 8,
};

function cancelButtonStyle(submitting: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    padding: '4px 12px',
    border: '1px solid var(--sp-border)',
    background: 'transparent',
    color: 'var(--sp-text-muted)',
    borderRadius: 3,
    cursor: submitting ? 'not-allowed' : 'pointer',
  };
}

export const eligibleTaskDispatcherWidget: WidgetDefinition = {
  id: 'eligible-task-dispatcher',
  name: 'Eligible Task Dispatcher',
  description: 'Per-agent eligibility view with per-task and bulk dispatch.',
  icon: 'play',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 12, h: 8 },
  minSize: { w: 6, h: 6 },
  component: EligibleTaskDispatcherWidget,
};
